import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ApiErrorSchema,
  ClauseCandidateDecisionRequestSchema,
  type ClauseCandidateDecisionRequest,
  type ClauseCandidateDecisionResponse,
  type ClauseCandidateDetailResponse,
  type ClauseCandidateVariant,
  type FieldError,
} from '@clm/shared-types';

import { decideClauseCandidate, fetchClauseCandidateDetail } from '../api/clause-candidates';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';

/**
 * The candidate detail panel — where a Legal reviewer turns a mined cluster
 * into a library clause, or records why not (CLM-53, FR-16/FR-17).
 *
 * ## Spans, never paragraph text (rule 13)
 *
 * `variants_in_scope` carries `{start_offset, end_offset}` into contracts the
 * caller may read, plus the normalised fingerprint. This panel renders those
 * and links to the contract record; it never asks for, and never shows, the
 * paragraph prose. The contract text is stored once, on `clm.contract`.
 *
 * ## `master_text` is typed, never prefilled (FR-17)
 *
 * The model proposes a *description* — code, title, category, purpose — and
 * those four are prefilled and editable. The authoritative clause wording is
 * the reviewer's alone, so the textarea opens empty and the hint says why.
 * `purpose_text_is_ai_proposal` is **computed** from whether the reviewer left
 * the model's purpose text unchanged; it is displayed, never a free checkbox,
 * because it decides whether the published row carries AI provenance
 * (`ck_clause_library_purpose_provenance`) and a person ticking it wrongly
 * would make the evidence chain lie.
 *
 * ## `seen_review_state` is what was on screen
 *
 * Sent from the detail the reviewer was shown, never recomputed. A stale
 * screen is a `409` — another reviewer settled this — rendered in the neutral
 * tone with a Reload action, never red.
 *
 * ## No role gating here (NFR-06)
 *
 * Every control renders for everyone. `mayAttempt` is a display hint only —
 * the "which your sign-in does not show" fragment appears only when the
 * sign-in names roles and Legal reviewer is not among them (D-82-1); the
 * gateway's 403 is a presented state, and in prod nobody holds
 * `legal_reviewer` yet (B-07), so that state is the honest one until the group
 * exists.
 */

/**
 * CLM-102 — variants shown at once. 25 matches the candidate list's own page
 * size above it, so the panel does not read as a different kind of table.
 */
const VARIANTS_PER_PAGE = 25;

type DetailPhase =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly detail: ClauseCandidateDetailResponse }
  | {
      readonly kind: 'problem';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
    };

type DecisionPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting' }
  | { readonly kind: 'settled'; readonly response: ClauseCandidateDecisionResponse }
  | {
      readonly kind: 'problem';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
      /**
       * The API's `validation_failed` message is a fixed sentence; the field
       * that failed lives only in `body.field_errors`. Kept so the panel can
       * name it (acceptance criterion 7). Empty for every other problem.
       */
      readonly field_errors: readonly FieldError[];
    };

type DecisionKind = 'accept' | 'reject' | 'merge' | 'split';

function similarity(value: number | null): string {
  return value === null ? 'not measured' : value.toFixed(2);
}

/** `field_errors` from a well-formed API error body; `[]` for anything else. */
function fieldErrorsOf(error: unknown): readonly FieldError[] {
  if (!(error instanceof ApiError)) return [];
  const parsed = ApiErrorSchema.safeParse(error.body);
  return parsed.success ? parsed.data.field_errors : [];
}

function nullIfBlank(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function CandidatePanel({
  cluster_id,
  joinThreshold,
  latestRunId,
  onClose,
  onSettled,
}: {
  readonly cluster_id: string;
  /** `latest_run.join_threshold` from the list, for the deviation comparison. */
  readonly joinThreshold: number | null;
  /**
   * `latest_run.mining_run_id` from the list (CLM-105) — so a cluster opened
   * from a superseded run (e.g. via "Current mining run only" turned off) can
   * be labelled as such, the same "tag out" convention `ContractRecord.tsx`
   * uses for `is_read_only`. `undefined` while the list hasn't learned the
   * latest run yet; that renders no tag rather than a wrong guess.
   */
  readonly latestRunId?: string | null;
  readonly onClose: () => void;
  /**
   * Called when the server's state has moved on — by this reviewer's decision
   * or, on a 409 Reload, by somebody else's — so the list re-fetches.
   */
  readonly onSettled: () => void;
}) {
  const { identity } = useIdentity();
  const isLegal = mayAttempt(identity, ['legal_reviewer']);

  const [detailPhase, setDetailPhase] = useState<DetailPhase>({ kind: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [decisionPhase, setDecisionPhase] = useState<DecisionPhase>({ kind: 'idle' });

  const [decision, setDecision] = useState<DecisionKind>('accept');
  const [clauseCode, setClauseCode] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [masterText, setMasterText] = useState('');
  const [purposeText, setPurposeText] = useState('');
  const [reason, setReason] = useState('');
  const [mergeInto, setMergeInto] = useState('');
  const [fieldErrors, setFieldErrors] = useState<readonly string[]>([]);
  /** CLM-102 — which page of `variants_in_scope` is on screen. Client-side: the
   *  detail response already carries the whole array, so paging it is display,
   *  not a second request. */
  const [variantPage, setVariantPage] = useState(0);

  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  // Focus moves to the panel heading on open (CLM-51), and every draft resets
  // when the cluster changes: a half-typed master text for cluster 7 must
  // never be submitted against cluster 8.
  //
  // CLM-102 — the panel renders *after* the candidate table and its pager, so on
  // a full page of 20 rows it opens well below the fold. Focus alone did not
  // move the viewport in practice and the button read as dead ("it is opening
  // but showing below"). `scrollIntoView` is what actually moves it; the focus
  // call stays because it is what carries a screen reader there, and the two are
  // doing different jobs. Guarded because jsdom does not implement it.
  useEffect(() => {
    headingRef.current?.focus();
    headingRef.current?.scrollIntoView?.({ block: 'start' });
    setDecisionPhase({ kind: 'idle' });
    setDecision('accept');
    setMasterText('');
    setReason('');
    setMergeInto('');
    setFieldErrors([]);
    setVariantPage(0);
  }, [cluster_id]);

  useEffect(() => {
    const controller = new AbortController();
    setDetailPhase({ kind: 'loading' });

    void (async () => {
      try {
        const detail = await fetchClauseCandidateDetail({ cluster_id }, controller.signal);
        if (controller.signal.aborted) return;
        setDetailPhase({ kind: 'ready', detail });
        // Prefill the four description fields from the model's proposal; the
        // reviewer confirms or changes each one. master_text is NOT here.
        const description = detail.candidate.description;
        setClauseCode(description.clause_code ?? '');
        setTitle(description.title ?? '');
        setCategory(description.category ?? '');
        setPurposeText(description.purpose_text ?? '');
      } catch (error) {
        if (controller.signal.aborted) return;
        setDetailPhase({
          kind: 'problem',
          problem: classifyApiError(error),
          detail: error instanceof ApiError ? error.message : null,
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
        });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [cluster_id, reloadNonce]);

  // A submitted decision is never aborted on unmount: the server commits the
  // decision and its audit row regardless, so the promise is left to settle
  // and `onSettled` still refreshes the list. Only the panel's own state
  // updates are skipped once it has gone.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  function reload() {
    setDecisionPhase({ kind: 'idle' });
    setReloadNonce((n) => n + 1);
    onSettled();
  }

  function submit(detail: ClauseCandidateDetailResponse) {
    const candidate = detail.candidate;
    const proposal = candidate.description.purpose_text;
    const submittedPurpose = nullIfBlank(purposeText);
    const purposeIsAiProposal = proposal !== null && submittedPurpose === proposal;

    const body: unknown = {
      cluster_id: candidate.cluster_id,
      // Exactly what this panel rendered. See the module note.
      seen_review_state: candidate.review_state,
      decision:
        decision === 'accept'
          ? {
              decision: 'accept',
              clause_code: clauseCode.trim(),
              title: title.trim(),
              category: nullIfBlank(category),
              master_text: masterText,
              purpose_text: submittedPurpose,
              purpose_text_is_ai_proposal: purposeIsAiProposal,
            }
          : decision === 'merge'
            ? { decision: 'merge', merge_into_cluster_id: mergeInto.trim(), reason: reason.trim() }
            : { decision, reason: reason.trim() },
    };

    // The contract's own schema is the client-side validation: a field error
    // here is rendered, never sent. What the API would answer 400 to, the
    // reviewer sees before the request exists.
    const parsed = ClauseCandidateDecisionRequestSchema.safeParse(body);
    if (!parsed.success) {
      setFieldErrors(
        parsed.error.issues.map((issue) => {
          const field = issue.path.filter((segment) => segment !== 'decision').join('.');
          if (field === 'master_text') {
            return 'Master text is required: type the authoritative wording. The model never proposes it (FR-17).';
          }
          if (field === 'reason') return 'A reason is required. A decision with no reason is one nobody can review.';
          if (field === 'merge_into_cluster_id') return 'Name the cluster this one merges into.';
          if (field === 'clause_code') {
            return 'Clause code: 2–32 characters, upper-case letters, digits and hyphens, starting with a letter or digit.';
          }
          return `${field === '' ? 'Decision' : field}: ${issue.message}`;
        }),
      );
      return;
    }
    setFieldErrors([]);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setDecisionPhase({ kind: 'submitting' });

    const request: ClauseCandidateDecisionRequest = parsed.data;
    void (async () => {
      try {
        const response = await decideClauseCandidate(request, controller.signal);
        if (controller.signal.aborted) return;
        if (mountedRef.current) setDecisionPhase({ kind: 'settled', response });
        onSettled();
      } catch (error) {
        if (controller.signal.aborted || !mountedRef.current) return;
        setDecisionPhase({
          kind: 'problem',
          problem: classifyApiError(error),
          detail: error instanceof ApiError ? error.message : null,
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
          field_errors: fieldErrorsOf(error),
        });
      }
    })();
  }

  const variantColumns: readonly DataTableColumn<ClauseCandidateVariant>[] = [
    {
      id: 'contract',
      header: 'Contract',
      cell: (variant) => (
        <Link to={`/contracts/record/${encodeURIComponent(variant.contract_key)}`}>
          <code>{variant.contract_key}</code>
        </Link>
      ),
    },
    { id: 'ref', header: 'Paragraph', cell: (variant) => variant.paragraph_ref ?? '—' },
    {
      id: 'span',
      header: 'Span',
      cell: (variant) => `chars ${String(variant.span.start_offset)}–${String(variant.span.end_offset)}`,
    },
    {
      id: 'sha',
      header: 'Fingerprint',
      cell: (variant) => (
        // The prefix on screen, the full digest in the title: enough to tell two
        // look-alike variants apart without a 64-character column.
        <code title={variant.paragraph_sha256}>{variant.paragraph_sha256.slice(0, 12)}</code>
      ),
    },
    {
      id: 'sim',
      header: 'Similarity',
      cell: (variant) =>
        variant.is_representative ? (
          <span className="tag">representative</span>
        ) : (
          similarity(variant.similarity_to_representative)
        ),
    },
    {
      id: 'restricted',
      header: 'Policy',
      cell: (variant) =>
        variant.processing_restricted ? <span className="tag out">restricted</span> : null,
    },
  ];

  const submitting = decisionPhase.kind === 'submitting';
  const settled = decisionPhase.kind === 'settled';
  const detail = detailPhase.kind === 'ready' ? detailPhase.detail : null;

  /* CLM-102 — cluster #30 carried 867 variants and the panel rendered every one:
   * printing the page produced 59 pages, 55 of them this single table. The rows
   * are already in hand, so this is a slice, not a request. The unpaged total
   * stays on screen above — a reviewer needs to know the cluster's real size
   * even while looking at 25 of it. */
  const pagedVariants = ((all: readonly ClauseCandidateVariant[]) => {
    const pageCount = Math.max(1, Math.ceil(all.length / VARIANTS_PER_PAGE));
    const page = Math.min(variantPage, pageCount - 1);
    const from = page * VARIANTS_PER_PAGE;
    const rows = all.slice(from, from + VARIANTS_PER_PAGE);
    return { rows, pageCount, firstShown: from + 1, lastShown: from + rows.length };
  })(detail?.variants_in_scope ?? []);
  const candidate = detail?.candidate ?? null;
  const description = candidate?.description ?? null;
  const purposeUnchanged =
    description !== null &&
    description.purpose_text !== null &&
    nullIfBlank(purposeText) === description.purpose_text;
  const decided = candidate !== null && candidate.review_state !== 'pending';

  return (
    <section className="vpanel" aria-labelledby="mr-panel-heading">
      <h3 id="mr-panel-heading" ref={headingRef} tabIndex={-1}>
        Candidate cluster {candidate === null ? cluster_id : `#${String(candidate.cluster_no)}`}
      </h3>
      <p className="vsub">
        <code>{cluster_id}</code>
        {candidate === null ? null : <> · run {candidate.mining_run_id} · {candidate.review_state}</>}
        {candidate !== null &&
        latestRunId !== undefined &&
        latestRunId !== null &&
        candidate.mining_run_id !== latestRunId ? (
          <>
            {' '}
            <span className="tag out">superseded run</span>
          </>
        ) : null}
      </p>

      {detailPhase.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Loading the candidate…
        </p>
      ) : null}

      {detailPhase.kind === 'problem' ? (
        <ErrorNotice
          kind={detailPhase.problem}
          detail={detailPhase.detail}
          correlation_id={detailPhase.correlation_id}
          action={
            <button type="button" className="btn" onClick={reload}>
              Retry
            </button>
          }
        />
      ) : null}

      {detail && candidate && description ? (
        <>
          <h4>Candidate</h4>
          <dl className="prov">
            <dt>Clause code</dt>
            <dd>{description.clause_code === null ? '—' : <code>{description.clause_code}</code>}</dd>
            <dt>Title</dt>
            <dd>{description.title ?? '—'}</dd>
            <dt>Category</dt>
            <dd>{description.category ?? '—'}</dd>
            <dt>Purpose</dt>
            <dd>
              {description.purpose_text ??
                (description.description_state === 'withheld_restricted'
                  ? 'withheld: every member is processing_restricted (§4C) — no model was called'
                  : description.description_state === 'refused_to_human'
                    ? 'the model refused; waiting for a person, not a second model'
                    : description.description_state === 'skipped_singleton'
                      ? 'no description — a singleton waits without a model call'
                      : 'no description yet')}
            </dd>
          </dl>

          <h4>Provenance</h4>
          <dl className="prov">
            <dt>Description state</dt>
            <dd>{description.description_state}</dd>
            {description.description_provider_id === null ? null : (
              <>
                <dt>Provider</dt>
                <dd>{description.description_provider_id}</dd>
              </>
            )}
            {description.description_model === null ? null : (
              <>
                <dt>Model</dt>
                <dd>{description.description_model}</dd>
              </>
            )}
            {description.description_generated_utc === null ? null : (
              <>
                <dt>Generated</dt>
                <dd>{description.description_generated_utc}</dd>
              </>
            )}
            <dt>Mining run</dt>
            <dd>{candidate.mining_run_id}</dd>
          </dl>

          <h4>Why it clusters</h4>
          <dl className="prov">
            <dt>Heading path</dt>
            <dd>
              {candidate.heading_path.length === 0
                ? 'no heading captured'
                : candidate.heading_path.join(' › ')}
            </dd>
            <dt>Paragraphs</dt>
            <dd>
              {candidate.member_count} across {candidate.distinct_contract_count} contract
              {candidate.distinct_contract_count === 1 ? '' : 's'}
            </dd>
            <dt>Mean similarity</dt>
            <dd>{similarity(candidate.deviation.mean_similarity)}</dd>
            <dt>Min similarity</dt>
            <dd>
              {similarity(candidate.deviation.min_similarity)}
              {joinThreshold === null || candidate.deviation.min_similarity === null
                ? ''
                : ` vs join threshold ${joinThreshold.toFixed(2)}${
                    candidate.deviation.min_similarity - joinThreshold < 0.05
                      ? ' — consider split'
                      : ''
                  }`}
            </dd>
            <dt>Distinct wordings</dt>
            <dd>{candidate.deviation.variant_count}</dd>
          </dl>

          <h4>Variants in scope</h4>
          <p className="mut">
            {detail.variants_in_scope_count} of {candidate.member_count} paragraphs are in contracts
            you may read. The count is scoped to you; another reviewer may see a different one for
            the same cluster.
          </p>
          <DataTable
            caption="Variants you may read"
            columns={variantColumns}
            rows={pagedVariants.rows}
            rowKey={(variant) => variant.contract_clause_id}
            empty={
              <p>
                None of this cluster’s paragraphs is in a contract you may read. The cluster is
                still reviewable; the spans are not yours to follow.
              </p>
            }
          />
          {pagedVariants.pageCount > 1 ? (
            <nav className="pager" aria-label="Variant pagination">
              <button
                type="button"
                className="btn sm"
                disabled={variantPage === 0}
                onClick={() => {
                  setVariantPage((page) => Math.max(0, page - 1));
                }}
              >
                Previous
              </button>
              <p className="pager-count">
                Variants {pagedVariants.firstShown}–{pagedVariants.lastShown} of{' '}
                {detail.variants_in_scope.length}
              </p>
              <button
                type="button"
                className="btn sm"
                disabled={variantPage >= pagedVariants.pageCount - 1}
                onClick={() => {
                  setVariantPage((page) => Math.min(pagedVariants.pageCount - 1, page + 1));
                }}
              >
                Next
              </button>
            </nav>
          ) : null}

          {settled ? null : (
            <form
              className="vform"
              onSubmit={(event) => {
                event.preventDefault();
                submit(detail);
              }}
            >
              {decided ? (
                <p>
                  Already {candidate.review_state} by {candidate.reviewed_by_upn ?? 'an unnamed reviewer'}
                  {candidate.reviewed_utc === null ? '' : ` at ${candidate.reviewed_utc}`}.
                </p>
              ) : null}
              <fieldset disabled={decided || submitting}>
                <legend>Decision</legend>
                {(['accept', 'reject', 'merge', 'split'] as const).map((kind) => (
                  <label key={kind} className="check">
                    <input
                      type="radio"
                      name="mr-decision"
                      value={kind}
                      checked={decision === kind}
                      onChange={() => {
                        setDecision(kind);
                        setFieldErrors([]);
                      }}
                    />
                    {kind === 'accept'
                      ? 'Accept — publish as a library clause'
                      : kind === 'reject'
                        ? 'Reject — not a library clause'
                        : kind === 'merge'
                          ? 'Merge — the same clause as another cluster'
                          : 'Split — a family of variants, re-cluster'}
                  </label>
                ))}

                {decision === 'accept' ? (
                  <>
                    <div className="field">
                      <label htmlFor="mr-clause-code">Clause code</label>
                      <input
                        id="mr-clause-code"
                        type="text"
                        autoComplete="off"
                        maxLength={32}
                        value={clauseCode}
                        onChange={(event) => setClauseCode(event.target.value)}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="mr-title">Title</label>
                      <input
                        id="mr-title"
                        type="text"
                        autoComplete="off"
                        maxLength={200}
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="mr-category">Category</label>
                      <input
                        id="mr-category"
                        type="text"
                        autoComplete="off"
                        maxLength={60}
                        value={category}
                        onChange={(event) => setCategory(event.target.value)}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="mr-master-text">Master text</label>
                      <textarea
                        id="mr-master-text"
                        rows={6}
                        value={masterText}
                        onChange={(event) => setMasterText(event.target.value)}
                      />
                      <p className="mut">
                        Type the authoritative wording — the model never proposes it (FR-17).
                      </p>
                    </div>
                    <div className="field">
                      <label htmlFor="mr-purpose-text">Purpose text</label>
                      <textarea
                        id="mr-purpose-text"
                        rows={3}
                        maxLength={1000}
                        value={purposeText}
                        onChange={(event) => setPurposeText(event.target.value)}
                      />
                      <p className="mut">
                        Purpose text:{' '}
                        {purposeUnchanged ? 'model’s description, unchanged' : 'edited by you'}
                        {' — '}
                        <code>purpose_text_is_ai_proposal</code> will be{' '}
                        <code>{purposeUnchanged ? 'true' : 'false'}</code>.
                      </p>
                    </div>
                  </>
                ) : null}

                {decision === 'merge' ? (
                  <div className="field">
                    <label htmlFor="mr-merge-into">Merge into cluster id</label>
                    <input
                      id="mr-merge-into"
                      type="text"
                      autoComplete="off"
                      maxLength={64}
                      value={mergeInto}
                      onChange={(event) => setMergeInto(event.target.value)}
                    />
                  </div>
                ) : null}

                {decision === 'accept' ? null : (
                  <div className="field">
                    <label htmlFor="mr-reason">Reason</label>
                    <textarea
                      id="mr-reason"
                      rows={3}
                      maxLength={1000}
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                    />
                  </div>
                )}

                <p className="mut">
                  Accept needs the Legal reviewer role
                  {isLegal ? '' : ', which your sign-in does not show'}; reject, merge and split
                  need Legal or Admin. The API decides on every call — a refusal is shown here, not
                  hidden.
                </p>
                <button type="submit" className="btn pri">
                  Record decision
                </button>
              </fieldset>
            </form>
          )}

          {fieldErrors.map((message) => (
            <p key={message} className="ferr" role="alert">
              {message}
            </p>
          ))}
        </>
      ) : null}

      <div className="voutcome" aria-live="polite">
        {decisionPhase.kind === 'submitting' ? <p>Recording your decision…</p> : null}

        {decisionPhase.kind === 'settled' ? (
          <div className="banner up" role="status">
            <p className="notice-head">
              <strong>Recorded as {decisionPhase.response.candidate.review_state}</strong>
            </p>
            <p className="mut">
              Audit row <code>{decisionPhase.response.audit.audit_id}</code> at{' '}
              {decisionPhase.response.audit.occurred_at_utc} by{' '}
              {decisionPhase.response.audit.actor_upn ?? 'an unnamed actor'}. That row is the
              evidence for this decision (NFR-07).
            </p>
            {decisionPhase.response.published_clause === null ? null : (
              <p>
                Published <code>{decisionPhase.response.published_clause.clause_code}</code> v
                {decisionPhase.response.published_clause.version_no} — searchable lexically now,
                semantically after <code>embed-clauses</code> runs. It has no embeddings until then.
              </p>
            )}
          </div>
        ) : null}

        {decisionPhase.kind === 'problem' ? (
          <ErrorNotice
            kind={decisionPhase.problem}
            detail={decisionPhase.detail}
            correlation_id={decisionPhase.correlation_id}
            action={
              decisionPhase.problem === 'conflict' ? (
                <button type="button" className="btn" onClick={reload}>
                  Reload
                </button>
              ) : undefined
            }
          />
        ) : null}
        {decisionPhase.kind === 'problem'
          ? decisionPhase.field_errors.map((fieldError) => (
              <p key={`${fieldError.path}:${fieldError.message}`} className="ferr" role="alert">
                {fieldError.path}: {fieldError.message}
              </p>
            ))
          : null}
      </div>

      <div className="vacts">
        <button type="button" className="btn qt" onClick={onClose}>
          Close
        </button>
      </div>
    </section>
  );
}
